import _ from 'lodash';
import { getIntl } from 'umi';

const formatEnumKey = (key, casing = 'lower') => {
  return casing === 'camel' ? _.camelCase(key) : String(key).toLowerCase();
};

const resolveEnumTranslationKey = (
  translationPrefix,
  enumKey,
  fallbackKey,
  casingPreference = ['lower', 'camel'],
) => {
  const intl = getIntl?.();
  const messages = intl?.messages || {};
  if (enumKey) {
    for (const casing of casingPreference) {
      const candidate = `${translationPrefix}.${formatEnumKey(enumKey, casing)}`;
      if (!casing || casing === 'lower') {
        if (messages[candidate]) return candidate;
      } else if (messages[candidate]) {
        return candidate;
      }
    }
  }
  return fallbackKey;
};
/**
 * Creates options array for select components from enum objects
 * @param {Object} enumObject - The enum object (e.g., AIProvider, RpaAgentPlatform)
 * @param {string} translationPrefix - The prefix for translation keys (e.g., 'agents.ai.provider')
 * @param {Object} [options]
 * @param {string} [options.casing='lower'] - 'lower' or 'camel' for translation key format
 * @param {boolean} [options.translateLabels=false] - If true, label is the translated text (enables search in Select); if false, label is the translation key
 * @param {Object} [options.intl] - Intl instance from useIntl(); when provided with translateLabels, ensures labels use the current locale after language change
 * @returns {Array} Array of options with value, label, and locale properties
 */
export const createEnumOptions = (enumObject, translationPrefix, options = {}) => {
  const { casing = 'lower', translateLabels = false, intl: intlFromContext } = options;
  const intl = translateLabels ? intlFromContext || getIntl?.() : null;
  const opts = Object.keys(enumObject)
    .map((key) => ({
      value: enumObject[key],
      label: `${translationPrefix}.${formatEnumKey(key, casing)}`,
      locale: !translateLabels,
    }))
    .sort((a, b) => a.value - b.value);
  if (translateLabels && intl) {
    return opts.map((o) => ({
      ...o,
      label: intl.formatMessage({ id: o.label }) || o.label,
      locale: false,
    }));
  }
  return opts;
};

/**
 * Gets the translation key for an enum value.
 * When value is undefined or null and nullable is false, it is treated as 0 (first enum value),
 * so backends that omit the property when it equals the default (e.g. Manual = 0) still resolve
 * to the correct label. Pass nullable true when the property can be genuinely absent.
 *
 * @param {Object} enumObject - The enum object
 * @param {number} [value] - The enum value (undefined/null → 0 unless nullable)
 * @param {string} translationPrefix - The prefix for translation keys
 * @param {boolean} [nullable=false] - If true, undefined/null returns null instead of resolving to 0
 * @returns {string|null} The translation key, or null when nullable and value is undefined/null
 */
export const getEnumTranslationKey = (enumObject, value, translationPrefix, nullable = false) => {
  if (nullable && (value === undefined || value === null)) {
    return null;
  }
  const actualValue = value === undefined || value === null ? 0 : value;
  const key = Object.keys(enumObject).find((k) => enumObject[k] === actualValue);
  return resolveEnumTranslationKey(translationPrefix, key, `${translationPrefix}.${actualValue}`);
};

/**
 * Builds a `fetchFn` for the EntitySelector backed by a local enum (no
 * backend round-trip). The picker calls this on open / search; we resolve
 * the human label via `getIntl()` so the dropdown reads in the current
 * locale and supports the popover's free-text filter.
 *
 * Useful for migrating multi-select enum dropdowns (e.g. OpenAI
 * `allowedTools`) to the EntitySelector tile UX while preserving the
 * payload contract (form value is `[{id: <enumValue>, name: <label>}]`,
 * which the caller unwraps to `[<enumValue>, ...]` for the backend).
 *
 * @param {Object} enumObject - Frozen enum map (PascalCase keys → number)
 * @param {string} translationPrefix - i18n prefix for the labels
 * @param {Object} [options] - Forwarded to `createEnumOptions`
 * @returns {(query: string) => Promise<{items: Array, totalCount: number}>}
 */
export const createEnumEntitySelectorFetcher = (
  enumObject,
  translationPrefix,
  options = {},
) => async (query = '') => {
  const intl = options.intl || getIntl?.();
  const opts = createEnumOptions(enumObject, translationPrefix, {
    ...options,
    translateLabels: true,
    intl,
  });
  // Optional per-item descriptions sit under `{prefix}.{key}.description`
  // (e.g. `agents.form.configurations.allowedTools.codeinterpreter.description`).
  // We resolve them by sniffing `intl.messages` directly — calling
  // `formatMessage` for keys that don't exist would emit "missing message"
  // warnings for every enum without a per-item description (most of them).
  const casing = options.casing || 'lower';
  const messages = intl?.messages || {};
  const descByValue = {};
  Object.keys(enumObject).forEach((key) => {
    const formattedKey = casing === 'camel' ? _.camelCase(key) : String(key).toLowerCase();
    const descKey = `${translationPrefix}.${formattedKey}.description`;
    if (messages[descKey]) {
      descByValue[enumObject[key]] = intl.formatMessage({ id: descKey });
    }
  });
  let items = opts.map((o) => ({
    id: o.value,
    name: o.label,
    ...(descByValue[o.value] ? { description: descByValue[o.value] } : {}),
  }));
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (q) {
    // Match against name OR description so users can search by either.
    items = items.filter(
      (it) =>
        String(it.name).toLowerCase().includes(q) ||
        (it.description && String(it.description).toLowerCase().includes(q)),
    );
  }
  return { items, totalCount: items.length };
};

/**
 * Specific helper for AI Provider options
 */
export const getAIProviderOptions = (enumObject) => {
  return createEnumOptions(enumObject, 'agents.ai.provider');
};

/**
 * Gets AI Provider translation key
 */
export const getAIProviderTranslationKey = (enumObject, value) => {
  return getEnumTranslationKey(enumObject, value, 'agents.ai.provider');
};

/**
 * Specific helper for RPA Platform options
 */
export const getRPAPlatformOptions = (enumObject) => {
  return createEnumOptions(enumObject, 'agents.rpa.platform');
};

/**
 * Gets RPA Platform translation key
 */
export const getRPAPlatformTranslationKey = (enumObject, value) => {
  return getEnumTranslationKey(enumObject, value, 'agents.rpa.platform');
};

/**
 * Specific helper for RPA Status options
 */
export const getRPAStatusOptions = (enumObject) => {
  return createEnumOptions(enumObject, 'agents.rpa.status');
};

/**
 * Gets RPA Status translation key
 */
export const getRPAStatusTranslationKey = (enumObject, value) => {
  return getEnumTranslationKey(enumObject, value, 'agents.rpa.status');
};

/**
 * Gets the enum name (key) from an enum value
 * If value is null or undefined, defaults to 0
 * @param {Object} enumObject - The enum object (e.g., OperationResultStatus, AutomyAIHandlerType)
 * @param {number|string} value - The enum value to find
 * @returns {string|undefined} The enum name as-is, or undefined if not found
 */
export const getEnumValue = (enumObject, value) => {
  const code = value !== null && value !== undefined ? parseInt(value, 10) : 0;
  const enumKey = Object.keys(enumObject).find((key) => enumObject[key] === code);
  return enumKey;
};

/**
 * Normalizes API / JSON input to the numeric value defined in a frozen enum object (PascalCase keys → numbers).
 * Accepts numbers, digit strings, and EnumMember-style strings (e.g. kebab-case from C# [EnumMember]).
 *
 * @param {Object} enumObject - Frozen map of key → number (e.g. LogicalOperator, CompanyAccessPolicyRuleType)
 * @param {*} raw - Value from API (number, `"11"`, `"ip-cidr"`, etc.)
 * @param {number} defaultValue - Fallback when missing or unrecognized
 * @returns {number}
 */
/**
 * Maps API value (number, numeric string, EnumMember kebab string) to numeric enum value, or undefined if invalid/absent.
 */
export const parseOptionalNumericEnumFromApi = (enumObject, raw) => {
  if (raw === null || raw === undefined || raw === '') return undefined;
  const validNumbers = new Set(Object.values(enumObject).filter((v) => typeof v === 'number'));
  const kebabNameToValue = {};
  for (const key of Object.keys(enumObject)) {
    const v = enumObject[key];
    if (typeof v !== 'number') continue;
    kebabNameToValue[_.kebabCase(key)] = v;
  }
  if (typeof raw === 'number' && validNumbers.has(raw)) return raw;
  const str = String(raw).trim();
  if (/^\d+$/.test(str)) {
    const n = Number(str);
    return validNumbers.has(n) ? n : undefined;
  }
  const kebab = _.kebabCase(str);
  if (kebabNameToValue[kebab] !== undefined) return kebabNameToValue[kebab];
  return undefined;
};

export const normalizeNumericEnumValue = (enumObject, raw, defaultValue) => {
  const parsed = parseOptionalNumericEnumFromApi(enumObject, raw);
  if (parsed !== undefined) return parsed;
  if (raw == null || raw === '') return defaultValue;
  return defaultValue;
};

/**
 * Normalizes API / JSON input to the canonical string value stored in a frozen enum object (string-valued enums).
 *
 * @param {Object} enumObject - Frozen map of key → string (e.g. ComparisonOperator)
 * @param {*} raw - Value from API
 * @param {string} defaultValue - Fallback when missing or unrecognized
 * @returns {string}
 */
export const normalizeStringEnumValue = (enumObject, raw, defaultValue) => {
  const valid = new Set(Object.values(enumObject).filter((v) => typeof v === 'string'));
  if (raw == null || raw === '') return defaultValue;
  const s = String(raw).trim();
  if (valid.has(s)) return s;
  const kebab = _.kebabCase(s);
  if (valid.has(kebab)) return kebab;
  return defaultValue;
};

/**
 * Transforms EnumMember values from C# backend to translation keys
 * @param {string} enumMemberValue - The EnumMember value (e.g., "api-key-in-header")
 * @returns {string} The transformed key for translations (e.g., "apikeyinheader")
 */
export const transformEnumMemberToKey = (enumMemberValue) => {
  return enumMemberValue
    .toLowerCase()
    .replace(/-/g, '') // remover guiones
    .replace(/\s+/g, ''); // remover espacios
};
